import { useState } from "react";

import { api } from "../lib/api";
import type { CategorySuggestion, Lane, LaneDraft, LanePreview } from "../lib/types";
import { money } from "./MatchCard";

export const MARKETPLACES = [
  "EBAY_AU",
  "EBAY_US",
  "EBAY_GB",
  "EBAY_DE",
  "EBAY_CA",
  "EBAY_NZ",
];

const BUYING_OPTIONS = ["FIXED_PRICE", "AUCTION", "BEST_OFFER"];

// eBay's standard condition ids. Shown by name, because typing an id from memory is exactly
// the mistake this editor exists to prevent.
const CONDITIONS: [string, string][] = [
  ["1000", "Brand New"],
  ["1500", "New (other)"],
  ["1750", "New with defects"],
  ["2000", "Certified refurbished"],
  ["2500", "Seller refurbished"],
  ["3000", "Used"],
  ["4000", "Very Good"],
  ["5000", "Good"],
  ["6000", "Acceptable"],
  ["7000", "For parts or not working"],
];

export const BLANK_LANE: LaneDraft = {
  name: "",
  category_ids: [],
  keywords: "",
  marketplace_id: "EBAY_AU",
  item_location_country: null,
  min_price: null,
  max_price: null,
  exclude_terms: [],
  condition_ids: null,
  exclude_condition_ids: null,
  buying_options: ["FIXED_PRICE", "AUCTION", "BEST_OFFER"],
  poll_interval_day: 60,
  poll_interval_night: 300,
  enabled: true,
  pause_when_unwatched: true,
};

export function callsPerDay(draft: { poll_interval_day: number; poll_interval_night: number }) {
  return Math.round((16 * 3600) / (draft.poll_interval_day || 60) +
    (8 * 3600) / (draft.poll_interval_night || 300));
}

export function toDraft(lane: Lane): LaneDraft {
  const { id: _id, last_polled_at: _p, last_seen_start_date: _w, created_at: _c, ...rest } = lane;
  return rest;
}

/**
 * Create or edit one lane.
 *
 * Two guard rails, because a misconfigured lane fails silently rather than loudly: category
 * ids are resolved through eBay's Taxonomy API rather than typed, and Preview runs the query
 * once so you can see real results before committing it to a 60-second loop.
 */
export default function LaneEditor({
  lane,
  onSaved,
  onCancel,
}: {
  lane: Lane | null;
  onSaved: (lane: Lane) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<LaneDraft>(lane ? toDraft(lane) : BLANK_LANE);
  const [categoryQuery, setCategoryQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CategorySuggestion[] | null>(null);
  const [preview, setPreview] = useState<LanePreview | null>(null);
  const [busy, setBusy] = useState<"" | "save" | "preview" | "categories">("");
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<LaneDraft>) => setDraft({ ...draft, ...patch });
  const fail = (err: unknown) => setError(err instanceof Error ? err.message : String(err));

  async function search() {
    setBusy("categories");
    setError(null);
    try {
      setSuggestions(await api.categories(categoryQuery.trim(), draft.marketplace_id));
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  async function runPreview() {
    setBusy("preview");
    setError(null);
    try {
      setPreview(await api.previewLane(draft));
    } catch (err) {
      fail(err);
      setPreview(null);
    } finally {
      setBusy("");
    }
  }

  async function save() {
    setBusy("save");
    setError(null);
    try {
      onSaved(lane ? await api.patchLane(lane.id, draft) : await api.createLane(draft));
    } catch (err) {
      fail(err);
    } finally {
      setBusy("");
    }
  }

  const addCategory = (id: string) =>
    !draft.category_ids.includes(id) && set({ category_ids: [...draft.category_ids, id] });

  return (
    <div className="card stack">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2 style={{ fontSize: 15 }}>{lane ? `Editing ${lane.name}` : "New lane"}</h2>
        <span className="pill">~{callsPerDay(draft)} calls/day</span>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="row">
        <div className="field" style={{ flex: 2, minWidth: 180 }}>
          <label htmlFor="l-name">Name</label>
          <input
            id="l-name"
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Xbox AU"
          />
        </div>
        <div className="field" style={{ flex: 1, minWidth: 140 }}>
          <label htmlFor="l-market">Marketplace</label>
          <select
            id="l-market"
            value={draft.marketplace_id}
            onChange={(e) => set({ marketplace_id: e.target.value })}
          >
            {MARKETPLACES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label htmlFor="l-country">Item location country</label>
        <input
          id="l-country"
          style={{ maxWidth: 120 }}
          maxLength={2}
          value={draft.item_location_country ?? ""}
          onChange={(e) =>
            set({ item_location_country: e.target.value.toUpperCase() || null })
          }
          placeholder="AU"
        />
        <div className="hint">
          The cheapest filter here: eBay drops foreign listings before they cost you anything.
          On EBAY_AU a broad query is ~90% US, JP and GB stock, and a watch that only wants
          local listings pays a classification for every one of them. Leave empty to see
          everything.
        </div>
      </div>

      <div className="field">
        <label htmlFor="l-keywords">Keywords</label>
        <input
          id="l-keywords"
          value={draft.keywords ?? ""}
          onChange={(e) => set({ keywords: e.target.value || null })}
          placeholder="xbox series"
        />
        <div className="hint">
          Keep these broad — recall beats precision here. The narrowing happens in a watch, for
          free; narrowing here costs you listings you never see.
        </div>
      </div>

      <div className="field">
        <label>Categories</label>
        <div className="row" style={{ marginBottom: 8 }}>
          {draft.category_ids.map((id) => (
            <span className="pill accent" key={id}>
              {id}
              <button
                className="ghost"
                style={{ padding: "0 4px", fontSize: 14, lineHeight: 1 }}
                onClick={() =>
                  set({ category_ids: draft.category_ids.filter((c) => c !== id) })
                }
                aria-label={`remove category ${id}`}
              >
                x
              </button>
            </span>
          ))}
          {!draft.category_ids.length && <span className="faint">none — keywords only</span>}
        </div>
        <div className="row">
          <input
            value={categoryQuery}
            onChange={(e) => setCategoryQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && categoryQuery.length > 1 && void search()}
            placeholder="search eBay categories, e.g. video game console"
            style={{ flex: 1 }}
          />
          <button onClick={() => void search()} disabled={categoryQuery.length < 2 || !!busy}>
            {busy === "categories" ? "..." : "Search"}
          </button>
        </div>
        <div className="hint">
          Resolved through eBay's Taxonomy API. Never type an id from memory — a wrong one polls
          nothing, forever, with no error.
        </div>
        {suggestions && (
          <div className="stack" style={{ gap: 6, marginTop: 8 }}>
            {!suggestions.length && <span className="faint">no categories matched</span>}
            {suggestions.slice(0, 8).map((s) => (
              <div className="row" key={s.category_id} style={{ gap: 8 }}>
                <button onClick={() => addCategory(s.category_id)} disabled={draft.category_ids.includes(s.category_id)}>
                  {draft.category_ids.includes(s.category_id) ? "added" : "add"}
                </button>
                <span className="mono faint">{s.category_id}</span>
                <span>{s.category_name}</span>
                <span className="faint">{s.path}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="row">
        <div className="field" style={{ width: 120 }}>
          <label htmlFor="l-min">Min price</label>
          <input
            id="l-min"
            type="number"
            value={draft.min_price ?? ""}
            onChange={(e) => set({ min_price: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
        <div className="field" style={{ width: 120 }}>
          <label htmlFor="l-max">Max price</label>
          <input
            id="l-max"
            type="number"
            value={draft.max_price ?? ""}
            onChange={(e) => set({ max_price: e.target.value ? Number(e.target.value) : null })}
          />
        </div>
        <div className="field" style={{ width: 130 }}>
          <label htmlFor="l-day">Day interval (s)</label>
          <input
            id="l-day"
            type="number"
            min={10}
            value={draft.poll_interval_day}
            onChange={(e) => set({ poll_interval_day: Number(e.target.value) })}
          />
        </div>
        <div className="field" style={{ width: 140 }}>
          <label htmlFor="l-night">Night interval (s)</label>
          <input
            id="l-night"
            type="number"
            min={10}
            value={draft.poll_interval_night}
            onChange={(e) => set({ poll_interval_night: Number(e.target.value) })}
          />
        </div>
      </div>

      <div className="field">
        <label>Buying options</label>
        <div className="row">
          {BUYING_OPTIONS.map((option) => (
            <label className="row" key={option} style={{ gap: 5, marginBottom: 0 }}>
              <input
                type="checkbox"
                style={{ width: "auto" }}
                checked={draft.buying_options.includes(option)}
                onChange={(e) =>
                  set({
                    buying_options: e.target.checked
                      ? [...draft.buying_options, option]
                      : draft.buying_options.filter((o) => o !== option),
                  })
                }
              />
              <span className="mono" style={{ fontSize: 12 }}>
                {option}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="l-exclude">Exclude terms (stage 0 kill-list, comma separated)</label>
        <input
          id="l-exclude"
          className="mono"
          value={draft.exclude_terms.join(", ")}
          onChange={(e) =>
            set({
              exclude_terms: e.target.value
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean),
            })
          }
          placeholder="controller, case, skin, faceplate"
        />
        <div className="hint">
          Free deterministic filter, applied before the model sees anything. Single words match
          whole words; phrases match as substrings.
        </div>
      </div>

      <div className="field">
        <label>Exclude conditions</label>
        <div className="row">
          {CONDITIONS.map(([id, name]) => {
            const excluded = (draft.exclude_condition_ids ?? []).includes(id);
            return (
              <label className="row" key={id} style={{ gap: 5, marginBottom: 0 }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={excluded}
                  onChange={(e) => {
                    const current = draft.exclude_condition_ids ?? [];
                    const next = e.target.checked
                      ? [...current, id]
                      : current.filter((c) => c !== id);
                    set({ exclude_condition_ids: next.length ? next : null });
                  }}
                />
                <span style={{ fontSize: 12.5 }}>{name}</span>
              </label>
            );
          })}
        </div>
        <div className="hint">
          The inverse of eBay's filter, applied in stage 0: everything <em>except</em> what you
          tick, including conditions not listed here. Excluding "Brand New" is the usual case.
          Prefer this to an inclusive list — sellers miscategorise constantly, and an inclusive
          list silently drops whatever they invented.
        </div>
      </div>

      <div className="field">
        <label htmlFor="l-conditions">Only these condition ids (inclusive, eBay-side)</label>
        <input
          id="l-conditions"
          className="mono"
          value={(draft.condition_ids ?? []).join(", ")}
          onChange={(e) => {
            const ids = e.target.value.split(",").map((t) => t.trim()).filter(Boolean);
            set({ condition_ids: ids.length ? ids : null });
          }}
          placeholder="empty = no condition filter"
        />
        <div className="hint">
          Leave empty unless you are certain. This narrows at eBay, so anything miscategorised
          never reaches you at all.
        </div>
      </div>

      <div className="row">
        <label className="row" style={{ gap: 6, marginBottom: 0 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={draft.enabled}
            onChange={(e) => set({ enabled: e.target.checked })}
          />
          Enabled (polling)
        </label>
        <label
          className="row"
          style={{ gap: 6, marginBottom: 0 }}
          title="Skip polling while no enabled watch listens to this lane. Turn off to collect data before writing a watch."
        >
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={draft.pause_when_unwatched}
            onChange={(e) => set({ pause_when_unwatched: e.target.checked })}
          />
          Idle when unwatched
        </label>
        <div className="spacer" />
        <button onClick={() => void runPreview()} disabled={!!busy}>
          {busy === "preview" ? "Querying eBay..." : "Preview (1 call)"}
        </button>
        <button className="ghost" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="primary"
          onClick={() => void save()}
          disabled={!draft.name || (!draft.keywords && !draft.category_ids.length) || !!busy}
        >
          {busy === "save" ? "Saving..." : lane ? "Save lane" : "Create lane"}
        </button>
      </div>

      {preview && (
        <div className="stack" style={{ gap: 8 }}>
          <div className="row">
            <span className={`pill ${preview.total ? "pass" : "risk"}`}>
              {preview.total.toLocaleString()} listings match
            </span>
            {!preview.total && (
              <span className="faint">
                Nothing matched — check the category id and keywords before saving.
              </span>
            )}
          </div>
          {preview.sample.map((item) => (
            <div className="row" key={item.item_id} style={{ gap: 8, alignItems: "baseline" }}>
              <span className="price" style={{ minWidth: 80 }}>
                {money(item.price, item.currency)}
              </span>
              <a href={item.url ?? "#"} target="_blank" rel="noreferrer">
                {item.title}
              </a>
              <span className="faint">
                {[item.condition_name, item.item_location].filter(Boolean).join(" - ")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
